import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createMeetChatOutgoingImageMessage,
  createMeetChatOutgoingMessage,
  isParticipantSystemLabel,
  mapTransportMessageToMeetChatMessage,
  type MeetChatAuthorContext,
} from './chat.mappers';
import { prepareMeetChatImageAttachment } from './chat.image';
import { MeetChatMessage, MeetChatState, MeetChatTransportMessage } from './types';

interface UseMeetChatArgs {
  localParticipantId: string;
  localParticipantName?: string;
  authorContext: MeetChatAuthorContext;
  sendMessage?: (message: {
    id: string;
    kind: 'text' | 'image';
    text: string;
    sentAt: string;
    senderName?: string;
    image?: {
      dataUrl: string;
      mimeType: string;
      width: number;
      height: number;
      file?: File;
    };
  }) => Promise<boolean> | boolean;
}

const appendUniqueMessage = (messages: MeetChatMessage[], nextMessage: MeetChatMessage) => {
  if (messages.some((message) => message.id === nextMessage.id)) {
    return messages;
  }

  return [...messages, nextMessage];
};

const mergePersistedAuthorNames = (
  persisted: Record<string, string>,
  participantDisplayNames: Record<string, string> | undefined,
) => {
  Object.entries(participantDisplayNames ?? {}).forEach(([participantId, name]) => {
    if (!isParticipantSystemLabel(name, participantId)) {
      persisted[participantId] = name.trim();
    }
  });
};

const buildAuthorContextWithPersistedNames = (
  authorContext: MeetChatAuthorContext,
  persistedAuthorNames: Record<string, string>,
): MeetChatAuthorContext => {
  const participantDisplayNames = { ...persistedAuthorNames };

  Object.entries(authorContext.participantDisplayNames ?? {}).forEach(([participantId, name]) => {
    if (!isParticipantSystemLabel(name, participantId)) {
      participantDisplayNames[participantId] = name.trim();
    }
  });

  return {
    ...authorContext,
    participantDisplayNames,
  };
};

const remapMessagesWithAuthorContext = (messages: MeetChatMessage[], authorContext: MeetChatAuthorContext) => {
  let hasChanges = false;

  const nextMessages = messages.map((message) => {
    const nextMessage = mapTransportMessageToMeetChatMessage(
      {
        id: message.id,
        kind: message.kind,
        text: message.text,
        sentAt: message.sentAt,
        senderId: message.authorId,
        senderName: message.wireSenderName,
        image: message.image,
      },
      authorContext,
    );

    const shouldKeepPreviousAuthorName =
      !nextMessage.isOwn &&
      !isParticipantSystemLabel(message.authorName, message.authorId) &&
      isParticipantSystemLabel(nextMessage.authorName, message.authorId);

    const resolvedMessage = shouldKeepPreviousAuthorName
      ? { ...nextMessage, authorName: message.authorName }
      : nextMessage;

    if (resolvedMessage.authorName !== message.authorName || resolvedMessage.isOwn !== message.isOwn) {
      hasChanges = true;
      return resolvedMessage;
    }

    return message;
  });

  return hasChanges ? nextMessages : messages;
};

export function useMeetChat({ localParticipantId, localParticipantName, authorContext, sendMessage }: UseMeetChatArgs) {
  const persistedAuthorNamesRef = useRef<Record<string, string>>({});
  const [state, setState] = useState<MeetChatState>({
    isOpen: false,
    draft: '',
    messages: [],
    isSendingImage: false,
    hasUnread: false,
  });

  const rememberAuthorName = useCallback((authorId: string, authorName: string) => {
    if (!isParticipantSystemLabel(authorName, authorId)) {
      persistedAuthorNamesRef.current[authorId] = authorName;
    }
  }, []);

  const getAuthorContext = useCallback(() => {
    mergePersistedAuthorNames(persistedAuthorNamesRef.current, authorContext.participantDisplayNames);

    return buildAuthorContextWithPersistedNames(authorContext, persistedAuthorNamesRef.current);
  }, [authorContext]);

  useEffect(() => {
    persistedAuthorNamesRef.current = {};
    setState((prevState) => ({
      ...prevState,
      messages: [],
      draft: '',
      isSendingImage: false,
      hasUnread: false,
    }));
  }, [localParticipantId]);

  const setDraft = useCallback((draft: string) => {
    setState((prevState) => ({
      ...prevState,
      draft,
    }));
  }, []);

  const toggleOpen = useCallback(() => {
    setState((prevState) => {
      const isOpen = !prevState.isOpen;

      return {
        ...prevState,
        isOpen,
        hasUnread: isOpen ? false : prevState.hasUnread,
      };
    });
  }, []);

  const close = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  }, []);

  const handleIncomingMessage = useCallback(
    (transportMessage: MeetChatTransportMessage) => {
      const resolvedAuthorContext = getAuthorContext();
      const nextMessage = mapTransportMessageToMeetChatMessage(transportMessage, resolvedAuthorContext);

      rememberAuthorName(nextMessage.authorId, nextMessage.authorName);

      setState((prevState) => {
        const messages = appendUniqueMessage(prevState.messages, nextMessage);
        const isNewMessage = messages !== prevState.messages;
        const shouldMarkUnread = isNewMessage && !nextMessage.isOwn && !prevState.isOpen;

        return {
          ...prevState,
          messages,
          hasUnread: shouldMarkUnread ? true : prevState.hasUnread,
        };
      });
    },
    [getAuthorContext, rememberAuthorName],
  );

  useEffect(() => {
    const resolvedAuthorContext = getAuthorContext();

    setState((prevState) => {
      const nextMessages = remapMessagesWithAuthorContext(prevState.messages, resolvedAuthorContext);

      nextMessages.forEach((message) => {
        if (!message.isOwn) {
          rememberAuthorName(message.authorId, message.authorName);
        }
      });

      if (nextMessages === prevState.messages) {
        return prevState;
      }

      return {
        ...prevState,
        messages: nextMessages,
      };
    });
  }, [authorContext, getAuthorContext, rememberAuthorName]);

  const send = useCallback(async () => {
    const trimmedDraft = state.draft.trim();

    if (!trimmedDraft || !sendMessage || !localParticipantId) {
      return false;
    }

    const outgoingMessage = createMeetChatOutgoingMessage(trimmedDraft, localParticipantName);
    const sent = await Promise.resolve(sendMessage(outgoingMessage));

    if (!sent) {
      return false;
    }

    const ownMessage = mapTransportMessageToMeetChatMessage(
      {
        ...outgoingMessage,
        senderId: localParticipantId,
      },
      getAuthorContext(),
    );

    setState((prevState) => ({
      ...prevState,
      draft: '',
      messages: appendUniqueMessage(prevState.messages, ownMessage),
    }));

    return true;
  }, [getAuthorContext, localParticipantId, localParticipantName, sendMessage, state.draft]);

  const sendImage = useCallback(
    async (file: File) => {
      if (!sendMessage || !localParticipantId) {
        return false;
      }

      setState((prevState) => ({
        ...prevState,
        isSendingImage: true,
      }));

      try {
        const imageAttachment = await prepareMeetChatImageAttachment(file);
        const outgoingMessage = createMeetChatOutgoingImageMessage(imageAttachment, localParticipantName);
        const sent = await Promise.resolve(sendMessage(outgoingMessage));

        if (!sent) {
          return false;
        }

        const ownMessage = mapTransportMessageToMeetChatMessage(
          {
            ...outgoingMessage,
            senderId: localParticipantId,
          },
          getAuthorContext(),
        );

        setState((prevState) => ({
          ...prevState,
          messages: appendUniqueMessage(prevState.messages, ownMessage),
        }));

        return true;
      } catch (error) {
        console.error('Failed to send chat image', error);
        return false;
      } finally {
        setState((prevState) => ({
          ...prevState,
          isSendingImage: false,
        }));
      }
    },
    [getAuthorContext, localParticipantId, localParticipantName, sendMessage],
  );

  return useMemo(
    () => ({
      isOpen: state.isOpen,
      draft: state.draft,
      messages: state.messages,
      isSendingImage: state.isSendingImage,
      hasUnread: state.hasUnread,
      hasMessages: state.messages.length > 0,
      setDraft,
      toggleOpen,
      close,
      send,
      sendImage,
      handleIncomingMessage,
    }),
    [
      close,
      handleIncomingMessage,
      send,
      sendImage,
      setDraft,
      state.draft,
      state.hasUnread,
      state.isOpen,
      state.isSendingImage,
      state.messages,
      toggleOpen,
    ],
  );
}

